<script setup>
import {loadStripe} from "@stripe/stripe-js";
import * as yup from "yup";
import {useI18n} from "vue-i18n";

const props = defineProps({
  id: {
    type: String,
    default: null,
  },
  stripeKey: {
    type: String,
    required: true,
  },
  formTitle: {
    type: String,
    required: true,
  },
  eventId: {
    type: String,
    required: false,
  },
  eventPrice: {
    type: String,
    required: true,
  },
  minTicketQuantity: {
    type: String,
    required: true,
  },
  maxTicketQuantity: {
    type: String,
    required: true,
  },
  isPaymentEnabled: {
    type: String,
    required: true,
  },
})
const route = useRoute()
const { $sentry } = useNuxtApp()
const contactId = ref('');
const isSubmitting = ref(false)
const formTitle = ref(props.formTitle);
const eventId = ref(props.eventId)
const eventPrice = ref(props.eventPrice)
const stripeLoaded = ref(false)
const publishableKey = ref(props.stripeKey)
const clientSecret = ref(null)
const requestErrors = ref('');
const isPaymentEnabled = ref(props.isPaymentEnabled === true);
const submitButtonLabel = computed(() => isPaymentEnabled.value ? 'Next' : 'Book Now');
const thankYouMessage = ref('Payment was successful!');
const neckerThankYouMessage = ref('Thank You! We will be in touch to complete your reservation');
const totalPrice = ref(props.eventPrice);
let step = ref(0)

const formatterUSD = new Intl.NumberFormat('en-US');

const fieldsDefaults = {
  firstName: '',
  lastName: '',
  email: '',
  quantity: 1
}
const fields = reactive({ ...fieldsDefaults })
const errors = reactive({ ...fieldsDefaults })
let stripe
let elements

const appearance = {
  theme: 'night',
  // labels: 'floating',
  variables: {
    colorPrimary: '#0570de',
    colorBackground: '#130725',
    colorText: '#ffffff',
    colorDanger: '#f2287d',
    fontFamily: 'Euclid Galactic, sans-serif',
    borderRadius: '16px',
    iconColor: '#7c55b8',
    colorTextPlaceholder: 'rgba(255, 255, 255, 0.3)',
  },

  rules: {
    '.Label': {
      color: '#ffffff',
      textTransform: 'uppercase',
      fontSize: '10px',
      letterSpacing: '0.27em',
      fontWeight: '600',
      marginLeft: '22px',
      marginBottom: '8px',
    },
    '.Input': {
      boxShadow: '0px',
      padding: '18px 22px',
      borderColor: '#1d083a',
    },
    '.Input:focus': {
      borderColor: '#c80afb',
      boxShadow: '0px',
    },
  },
}
const fonts = [
  {
    family: 'Euclid Galactic',
    src: 'url(https://www.virgingalactic.com/assets/fonts/EuclidGalactic-2G-WebXL.woff2)',
    weight: '400',
  },
  {
    family: 'Euclid Galactic',
    src: 'url(https://www.virgingalactic.com/assets/fonts/EuclidGalactic-3G-WebXL.woff2)',
    weight: '500',
  },
]
const runtimeConfig = useRuntimeConfig()
const baseURL = runtimeConfig.STRIPE_WORKER_URI;
const minQuantity = reactive(Number(props.minTicketQuantity) >= 1 ? props.minTicketQuantity : 1);
const maxQuantity = reactive(Number(props.maxTicketQuantity) >= 1 ? props.maxTicketQuantity : undefined);
const { t } = useI18n()
const schema = yup.object({
  email: yup
    .string()
    .email(t('form.errors.emailInvalid'))
    .required(t('form.errors.emailRequired')),
  firstName: yup
    .string().matches(/^\w{2}/)
    .required(),
  lastName: yup
    .string().matches(/^\w{2}/)
    .required(),
  quantity: yup
    .string()
});



const handleSubmit = async () => {
  const payload = {
    firstName : fields.firstName,
    lastName: fields.lastName,
    email: fields.email,
    eventId: eventId.value,
    eventPrice: eventPrice.value,
    quantity: fields.quantity
  };


  const areFieldsValid = await validateFields(fields);
  isSubmitting.value = true;

  if (!areFieldsValid) {
    isSubmitting.value = false;
    return;
  }


  if (!isPaymentEnabled.value) {

    console.log('creating attendee without payment ', payload);

    try {
      const response = await fetch(`${baseURL}/attendee/create`, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(payload),
      });

      if (response) {
        const data = await response.json();
        console.log(data)
        if (data && 'attendeeId' in data) {
          thankYouMessage.value = 'Your booking is complete!';
          requestErrors.value = '';
          step.value = 2;
        }
        isSubmitting.value = false;
      }
    } catch (ex){
      const errorNumber = useRandom();
      requestErrors.value = `Failed to create booking - error: ${errorNumber}`;
      $sentry.captureException( Error(`[${errorNumber}] Failed to create attendee ${ex.toString()}`));
    }

    return;
  }

  switch (step.value) {
    case 0:
      try {
        const response = await fetch(`${baseURL}/create`, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          method: 'POST',
          body: JSON.stringify(payload),
        });

        if (response){

          const data = await response.json();

          if (!data.attendeeId){
            const errorNumber = useRandom();
            requestErrors.value = `Could not complete booking - server error: ${errorNumber}`;
            $sentry.captureException( Error(`[${errorNumber}] Failed to get information from worker ${JSON.stringify(data)}`));
            return;
          }

          contactId.value = data.attendeeId;

          console.log('response: ', data);
          StripeInit(data.client_secret).catch((ex) => {
            console.error('failed to init stripe', ex);
          });

          if (data.errors) {
            console.log(data);
            requestErrors.value = data.errors;
            const errorNumber = useRandom();
            requestErrors.value = `Could not complete booking - try again: ${errorNumber}`;
            $sentry.captureException( Error(`[${errorNumber}] Failed to create attendee ${JSON.stringify(data.errors)}`));
            isSubmitting.value = false;
            return;
          }

          step.value++;
          isSubmitting.value = false;
          requestErrors.value = '';

        }
      } catch (ex){
        const errorNumber = useRandom();
        requestErrors.value = `Could not complete booking - error: ${errorNumber}`;
        $sentry.captureException( Error(`[${errorNumber}] Failed to create attendee ${ex.toString()}`));
      }
      break;
    case 1:
        try {
          const stripeResponse = await stripe.confirmPayment({
            elements,
            redirect: 'if_required',
            confirmParams: {
              return_url: 'https://virgingalactic.com',
            },
          });

          const { error, paymentIntent } = stripeResponse;

          const attendeeData = {
            attendee: contactId.value,
            eventId: eventId.value,
            eventPrice: eventPrice.value,
            transaction_id: paymentIntent.id,
            transaction_amount: paymentIntent.amount,
            transaction_status: paymentIntent.status,
            transaction_timestamp: paymentIntent.created,
            quantity: fields.quantity
          };

          await fetch(`${baseURL}/confirm`, {
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json',
            },
            method: 'POST',
            body: JSON.stringify(attendeeData),
          });

          step.value++;
          isSubmitting.value = false;
          requestErrors.value = '';
        } catch (ex){
          const errorNumber = useRandom();
          requestErrors.value = `Could not complete booking - error: ${errorNumber}`;
          $sentry.captureException( Error(`[${errorNumber}] Failed to complete payment ${ex.toString()}`));
        }
      break;

  }
};
const StripeInit = async (client_secret) => {
  console.log('initializing stripe');
  clientSecret.value = client_secret

  const stripePromise = loadStripe(publishableKey.value).catch((error) => {
    console.error(error)
  })

  stripe = await stripePromise

  elements = stripe.elements({clientSecret: client_secret, appearance, fonts})
  const paymentElement = elements.create('payment')
  paymentElement.mount('#checkout')
  stripeLoaded.value = true
}
const validateFields = async (payload) => {
  let isValid = true;

  for (const [key, value] of Object.entries(payload)) {
    try {
      await schema.validateAt(key, payload);
      errors[key] = false;
    } catch (ex) {
      console.log(key, ' is invalid');
      isValid = false;
      errors[key] = true;
    }
  }

  return isValid;
}
const validateQuantity = () => {
  if (fields.quantity >= maxQuantity) {
    requestErrors.value = `A maximum of ${maxQuantity} tickets can be purchased per person.`;
    totalPrice.value = formatterUSD.format(Number(eventPrice.value) * Number(fields.quantity));
    return;
  } else {
    requestErrors.value = '';
  }

  console.log(eventPrice.value);
  console.log(Number(fields.quantity));
  totalPrice.value = formatterUSD.format(Number(eventPrice.value) * Number(fields.quantity));
}
const validateInput = async (event) => {
  await validateFields(fields);
}

</script>

<template>
  <section class="stripe-module" :id='id'>
    <div class="form-container">
      <p v-if="!stripeKey" class="has-error">
        Stripe Key Required
      </p>
      <div class="text-center" v-if="step === 2">
        <h2 v-if="route.path != '/Necker%202025'">Thank You!</h2>
        <p v-if="route.path == '/Necker%202025'">{{neckerThankYouMessage}}</p>
        <p v-else>{{thankYouMessage}}</p>

      </div>
      <div class="title" v-if="step !== 2">
        <div v-if="formTitle">
          <h2>{{formTitle}}</h2>
          <div class="form__divider" ></div>
        </div>
      </div>
      <div class="step" v-if="step === 0">
        <div class="personal-info">
          <div>
            <input
              type="text"
              required
              @blur="validateInput"
              :class="['input__text', { 'has-error': errors.firstName }]"
              placeholder="First Name"
              v-model="fields.firstName"
            />
            <input
              type="text"
              required
              @blur="validateInput"
              :class="['input__text', { 'has-error': errors.lastName }]"
              placeholder="Last Name"
              v-model="fields.lastName"
            />
          </div>
          <div>
            <input
              type="text"
              required
              @blur="validateInput"
              :class="['input__text', { 'has-error': errors.email }]"
              placeholder="Email"
              v-model="fields.email"
            />
            <input
              type="number"
              required
              :min="minQuantity"
              @change="validateQuantity"
              :max="maxQuantity"
              :class="['input__text']"
              v-model="fields.quantity"
            />
          </div>
        </div>
      </div>
      <div class="step" v-if="step === 1">
        <div id="checkout"></div>
      </div>
      <div class="payment-actions" v-if="step !== 2">
        <div>
          <p v-if="isPaymentEnabled" ><span class="currency-symbol">$</span><span class="total-price"> {{totalPrice}}</span></p>
        </div>
        <div class="action-btn">
          <ButtonPill v-if="isSubmitting" class="nxtgen__submit" disabled>
          Submitting
        </ButtonPill>
          <ButtonPill
            v-else
            class="nxtgen__submit"
            icon="chevron-right"
            animate
            useGradient
            @click="handleSubmit"
          >
            {{ route.path == '/Necker%202025' ? 'Register Interest' : submitButtonLabel }}
          </ButtonPill></div>
      </div>
      <div v-if="requestErrors">
        <p>{{requestErrors}}</p>
      </div>
    </div>
  </section>
</template>

<style lang="scss">
@import 'styles/vendors/sass-mq/mq';
@import 'styles/utils/functions';
.payment-actions {
  margin-top: 1.5rem;
  display: flex;
  gap: 2rem;
  justify-items: center;
}


@media (min-width:1025px) {
  .payment-actions > div {
    display: flex;
    width: 50%;
    justify-content: center;
    align-items: center;
  }
  .action-btn button {
    width: 100%;
  }
  .action-btn button .button-pill__content {
    padding-left: 2em;
  }
}
.currency-symbol {
  font-size: 24px;
}
.total-price  {
  font-size: 32px;
}

.form-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.5rem;
}
.form-container h2 {
  text-align:  center;
}
.text-center {
  text-align: center;
}

.form-container .form__divider {
  padding:  0;
  margin-top: 0;
}

.stripe-module {
  width: 100%;
  margin: 0 auto;


  @include mq($from: 'm') using ($from) {
    max-width: gs(5, 4, 'xxxs');
  }

  @include mq($from: 'l') using ($from) {
    max-width: gs(12, 11, 'l');
  }
}

.personal-info {
  display: flex;
  gap: 2rem;
  justify-items: center;
}
.personal-info div {
  width: 100%;
  gap: 1rem;
  display: flex;
  flex-direction: column;
}
</style>
